import DeleteIcon from "@assets/icons/delete.svg?react";
import { useTranslation } from "@vixi/localization";
import { CourseData } from "@vixi/types";
import { ComponentProps, useCallback, useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { EditableBox, CourseStepPaddingWrapper, CourseStepDivider } from "..";
import _ from "lodash";
import { CourseTabComponentProps } from "@vixi/types";
import { CourseTabTitle } from "../../CourseTabs/CourseTabTitle";
import { useCourseEditorContext } from "../../context";
import { cn } from "@/lib/utils";
import useUiStore from "@/store/ui.state";
import { TransparentTextArea } from "@/components/input/text/textArea";
import { Button } from "@/components/base/button";

// Styling
const AnswerBox = (props: ComponentProps<typeof EditableBox>) => (
  <EditableBox {...props} className={cn("mt-0 flex flex-1 items-center py-1", props.className)} />
);

// Types
export interface MatchingStepProps extends CourseTabComponentProps, CourseData.MatchingQuestion {}

interface MatchingQuestionFormValues {
  question: string;
  answers: CourseData.MatchingPair[];
}

export function MatchingStep(props: MatchingStepProps) {
  const { t } = useTranslation();
  const { setValue } = useCourseEditorContext();
  const path = useMemo(() => props.path.concat("matching"), [props.path]);
  const { mode } = useUiStore((state) => state.courseEditor);
  const isDisabled = mode !== "edit";

  const { control, register, getValues, watch } = useForm<MatchingQuestionFormValues>({
    defaultValues: {
      question: props.question,
      answers: props.answers,
    },
  });

  const { append: appendPair, remove: removePair } = useFieldArray({
    name: "answers",
    control: control,
  });

  const addPair = () => {
    appendPair({ question: "item 1", answer: "item 2" });
  };

  // Update the global state with the current form values
  const updateState = useCallback(() => {
    const setValueOptions = {
      condition: (_prev?: CourseData.MatchingQuestion, parent?: CourseData.MatchingStep) =>
        parent?.id === props.id,
    };

    setValue(path, getValues(), setValueOptions);
  }, [path, getValues]);
  const debouncedUpdate = useMemo(() => _.debounce(updateState, 500), [updateState]);

  useEffect(() => {
    return updateState;
  }, []);
  watch(async () => debouncedUpdate());

  const answers = watch("answers");

  return (
    <>
      <CourseTabTitle className="mx-auto w-fit">{t("Question.Matching")}</CourseTabTitle>

      <CourseStepPaddingWrapper>
        <EditableBox>
          <TransparentTextArea
            className="fs-20"
            minRows={2}
            maxRows={4}
            disabled={isDisabled}
            {...register("question", { shouldUnregister: false })}
          />
        </EditableBox>
      </CourseStepPaddingWrapper>

      <CourseStepDivider />

      <CourseStepPaddingWrapper>
        <div className="flex min-h-0 flex-col gap-5 overflow-auto">
          {answers.map((_pair, pairIndex) => (
            <div key={pairIndex} className="flex items-center gap-2">
              <div className="flex flex-1 flex-col gap-0.75">
                <AnswerBox>
                  <TransparentTextArea
                    className="fs-20 min-w-0"
                    minRows={1}
                    maxRows={2}
                    disabled={isDisabled}
                    {...register(`answers.${pairIndex}.question` as const)}
                  />
                </AnswerBox>

                <AnswerBox>
                  <TransparentTextArea
                    className="fs-20 min-w-0"
                    minRows={1}
                    maxRows={2}
                    disabled={isDisabled}
                    {...register(`answers.${pairIndex}.answer` as const)}
                  />
                </AnswerBox>
              </div>

              {answers.length > 3 && (
                <Button
                  size="icon"
                  onClick={() => removePair(pairIndex)}
                  className="text-foreground hover:text-error"
                  disabled={isDisabled}
                >
                  <DeleteIcon />
                </Button>
              )}
            </div>
          ))}
        </div>

        {answers.length < 3 && (
          <Button
            variant="outlined"
            size="pill"
            className="mt-5 self-center"
            onClick={addPair}
            disabled={isDisabled}
          >
            {t("Question.AddMatchingPair")}
          </Button>
        )}
      </CourseStepPaddingWrapper>
    </>
  );
}

export default MatchingStep;
